import * as React from "react";
import { includes, memoize } from "lodash/fp";
import { merge, $, media } from "glamor";
import theme from "styles/theme";
import { Kinds, ValueTypes, Item } from "constants/product-collection";
import { Map, fromJS, Set } from "immutable";
import { TextField, NumberField, SelectField } from "./";
import { Form } from "formsy-react";
import { DATATEST } from "constants/integration";
import { amphibiousGet } from "helpers/objUtils";
import {
    dataGetter,
    getGeneratedFormulaValueGetter,
    parseAssortmentName,
} from "../../../../helpers/product-collection";
import { CDP_CELL_CHANGE } from "constants/analytics";
import { IGridContext } from "..";
import { DateField } from "./date-field";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { formatMessage } from "lib/globalize-util";
interface IProductDetailsProps {
    item: Map<string, any>; // Ag-grid rowNode reference to this row
    readOnly: boolean;
    schema: any;
    onUpdateItem?: any;
    itemsId?: string;
    itemId?: string;
    items: any;
    hashtags?: Set<string>;
    allowedValueFields: any;
    isServerRollup?: boolean;
    context?: Partial<IGridContext>;
}

export class ProductDetails extends React.PureComponent<IProductDetailsProps, any> {
    static defaultProps = {
        hashtags: Set(),
        allowedValueFields: [],
    };

    private form;
    private fieldRefs = {};
    private styles = merge(
        {
            padding: "0",
            position: "relative",
            color: theme.palette.darkGrey,
            maxWidth: "692px",
        },
        $(" .fieldContainer", {
            display: "flex",
            width: "100%",
            borderTop: `1px solid ${theme.palette.borderColor}`,
        }),
        $(" .fieldContainer:last-child", {
            borderBottom: `1px solid ${theme.palette.borderColor}`,
        }),
        $(" .fieldLabel", {
            flexGrow: "1",
            maxWidth: "190px",
            minWidth: "190px",
            padding: "10px 12px",
            backgroundColor: theme.palette.lightestGrey,
            color: theme.palette.darkGrey,
            fontSize: "14px",
            fontWeight: "700",
        }),
        $(" .error .fieldLabel", {
            color: theme.palette.error,
            backgroundColor: theme.palette.errorBackgroundColor,
        }),
        $(" .fieldContainer input", {
            color: theme.palette.textColor,
            padding: "10px 10px",
            border: "none",
            width: "100%",
        }),
        $(" .fieldContainer select", {
            color: theme.palette.textColor,
            margin: "10px 2px",
            border: "none",
            width: "98%",
            background: theme.palette.canvasColor,
        }),
        $(" .fieldContainer select option[disabled]", {
            display: "none",
        }),
        $(" .fieldContainer select:hover", {
            background: theme.palette.editing,
        }),
        $(" .fieldContainer select:focus", {
            background: theme.palette.editing,
        }),
        $(" .fieldContainer:hover select", {
            background: theme.palette.editing,
        }),
        $(" .fieldContainer .displayPlaceholderText", {
            color: theme.palette.grey,
        }),
        $(" .fieldContainer.nonEditable, .fieldValue.nonEditable", {
            padding: "10px 10px",
        }),
        $(" .fieldContainer.editable", {
            width: "100%",
        }),
        $(" .fieldContainer .errorMessage", {
            color: theme.palette.error,
            fontSize: "10px",
            margin: "0px 0px 6px 10px",
        }),
        $(" .fieldContainer.isFocused, .fieldContainer.isFocused, .fieldContainer.isFocused input", {
            backgroundColor: theme.palette.editing,
        }),
        $(" .fieldContainer.editable:hover, .fieldContainer.editable:hover .fieldContainer.editable:hover input", {
            backgroundColor: theme.palette.editing,
        }),
        $(".isMobile & .fieldContainer input", {
            fontSize: "16px",
        }),
        $(" .inputContainer", {
            width: "100%",
            fontSize: "14px",
        }),
        $(" .inputContainer select", {
            marginLeft: "7px",
        }),
        $(".isMobile & .inputContainer", {
            fontSize: "16px",
        }),
        $(" .DraftEditor-editorContainer", {
            fontSize: "14px",
        }),
        $(" .public-DraftEditorPlaceholder-root", {
            color: theme.palette.placeholderText,
            fontSize: "14px",
        }),
        $(".isMobile & .DraftEditor-editorContainer", {
            fontSize: "16px",
        }),
        $(".isMobile & .public-DraftEditorPlaceholder-root", {
            fontSize: "16px",
        }),
        $(" ::placeholder, ::-moz-placeholder", {
            color: theme.palette.placeholderText,
            fontSize: "14px",
            opacity: 1,
        }),
        media(
            "(max-width: 480px)",
            $(" .fieldLabel", {
                fontSize: "12px",
                padding: "12px",
                minWidth: "96px",
                maxWidth: "96px",
            }),
            $(" .fieldValue", {
                fontSize: "12px",
            }),
            $(" .inputContainer", {
                fontSize: "12px",
            }),
            $(" .DraftEditor-editorContainer", {
                fontSize: "12px",
            }),
            $(" .fieldContainer > .DraftEditor-root", {
                fontSize: "12px",
            }),
            $(" ::placeholder", {
                color: theme.palette.placeholderText,
                fontSize: "12px",
            }),
            $(" .public-DraftEditorPlaceholder-root", {
                fontSize: "12px",
            })
        )
    );

    handleGoToNextField = (field: any, backward?: boolean) => {
        const { schema } = this.props;
        const currentFieldName = field.get("field_name");
        const currentFieldIndex = schema.findIndex((schemaField) => {
            return schemaField.get("field_name") === currentFieldName;
        });
        const isIgnoredKind = includes([Kinds.FORMULA, Kinds.INLINE_FORMULA, Kinds.RATING_STAR, Kinds.IMAGE_URL]);
        const nextField = backward
            ? schema.findLast((item, index) => index < currentFieldIndex && !isIgnoredKind(item.get("kind")))
            : schema.find((item, index) => index > currentFieldIndex && !isIgnoredKind(item.get("kind")));
        if (nextField) {
            const nextFieldName = nextField.get("field_name");
            if (this.fieldRefs[nextFieldName]) {
                this.fieldRefs[nextFieldName].focus();
            } else {
                if (this.form.querySelector(`input[name='${nextFieldName}']`)) {
                    this.form.querySelector(`input[name='${nextFieldName}']`).focus();
                } else if (this.form.querySelector(`select[name='${nextFieldName}']`)) {
                    this.form.querySelector(`select[name='${nextFieldName}']`).focus();
                }
            }
        }
    };

    handleChangeItem = ({ field, value }) => {
        const { itemsId, itemId } = this.props;
        let item: any = this.props.item;
        const key = field.get("field_name");
        const valueType = field.get("value_type");

        item = item.toJS();

        if (!value) {
            item.fields[key] = { string_value: "" };
        } else if (valueType === ValueTypes.DATE) {
            item.fields[key] = { date_value: value };
        } else if (valueType === ValueTypes.BOOL) {
            item.fields[key] = { bool_value: value };
        } else {
            item.fields[key] = { string_value: value };
        }
        item = fromJS(item);
        const index = this.getRowIndexById(itemId);
        this.props.onUpdateItem?.(itemsId, index, item);

        const analytics = (global as any).analytics as any;
        analytics.track(CDP_CELL_CHANGE, {
            name: key,
        });
    };

    getFieldComponents() {
        const { item, readOnly, schema, hashtags, allowedValueFields, isServerRollup, context } = this.props;
        if (!item) {
            return null;
        }

        const components = [];
        (schema || []).forEach((field) => {
            let FieldComponent;
            const kind = field.get("kind");
            const valueType = field.get("value_type");
            switch (kind) {
                case Kinds.FORMULA:
                    FieldComponent = valueType === ValueTypes.NUMBER ? NumberField : TextField;
                    break;
                case Kinds.INLINE_FORMULA:
                case Kinds.RATING_STAR:
                case Kinds.IMAGE_URL:
                    // intentionally ignored
                    break;
                case Kinds.MULTILINE_TEXT:
                    FieldComponent = TextField;
                    break;
                case Kinds.DEFAULT_KIND:
                default:
                    if (ValueTypes.NUMBER === valueType) {
                        FieldComponent = NumberField;
                    } else if (ValueTypes.BOOL === valueType || field.getIn(["constraint", "allowed_values"])) {
                        FieldComponent = SelectField;
                    } else if (ValueTypes.DATE === valueType) {
                        FieldComponent = DateField;
                    } else {
                        FieldComponent = TextField;
                    }
                    break;
            }

            if (FieldComponent) {
                const fieldName = field.get("field_name");
                const freeform = field.getIn(["constraint", "freeform"]);
                const readOnlyField = [Kinds.SIZES_SELECTED, Kinds.SIZE_PROFILE_SELECTED].includes(kind)
                    ? true
                    : field.getIn(["display", "read_only"]);

                let value;
                if (kind === Kinds.FORMULA) {
                    const getter = getGeneratedFormulaValueGetter(field);
                    const rawValue = getter(field, { data: item?.toJS(), context: { ...context, isServerRollup } });
                    value = valueType === ValueTypes.NUMBER ? rawValue : `${rawValue}`; // We force a string here, as TextField expects that type of value
                } else if (kind === Kinds.ASSORTMENT_LINK_KIND) {
                    value = item.getIn(["fields", fieldName, "string_value"]);
                    value = parseAssortmentName(
                        value || "",
                        amphibiousGet(field, "field_value_delimiter")
                    ).assortmentName;
                } else if (valueType === ValueTypes.DATE) {
                    const valueField = "date_value";
                    const valueMap = item.getIn(["fields", fieldName, valueField]);
                    value = valueMap?.get("month")
                        ? `${valueMap?.get("month")}/${valueMap?.get("day")}/${valueMap?.get("year")}`
                        : "";
                } else if (valueType === ValueTypes.BOOL) {
                    value = !item.getIn(["fields", fieldName, "bool_value"])
                        ? formatMessage("No")
                        : formatMessage("Yes");
                } else {
                    const valueField =
                        isServerRollup && valueType === ValueTypes.NUMBER ? "number_value" : "string_value";
                    value = item.getIn(["fields", fieldName, valueField]);
                }

                components.push(
                    <FieldComponent
                        freeform={freeform}
                        dataGetter={dataGetter}
                        item={item}
                        handleGoToNextField={this.handleGoToNextField}
                        key={fieldName}
                        field={field}
                        value={value}
                        readOnly={readOnly || readOnlyField}
                        onChange={this.handleChangeItem}
                        onKeyDown={this.handleKeyDown(field)}
                        bindEditor={this.handleSetEditorRef}
                        hashtags={hashtags}
                        allowedValueFields={allowedValueFields}
                        valueType={valueType}
                    />
                );
            }
        });

        return components;
    }

    render() {
        const { item } = this.props;

        if (!item) {
            return null;
        }

        return (
            <div
                data-test={DATATEST.ASSORTMENTS.DETAILS_PRODUCT_FORM}
                className="productDetails"
                {...this.styles}
                ref={this.setRef}
            >
                <Form>{this.getFieldComponents()}</Form>
            </div>
        );
    }

    private setRef = (ref) => {
        this.form = ref;
    };

    private handleSetEditorRef = (ref: any, key: string) => {
        this.fieldRefs[key] = ref;
    };

    private getRowIndexById = (id) => {
        // gets row index determined by collection not ag-grid
        return this.props.items.findIndex((item) => item.getIn(Item.FIELDS.ID) === id);
    };

    // tslint:disable-next-line: member-ordering
    private handleKeyDown = memoize((field) => (e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case KEYBOARD_KEYS.ENTER:
                process.nextTick(() => this.handleGoToNextField(field));
                e.preventDefault();
                break;
            case KEYBOARD_KEYS.TAB:
                const backward = !!e.shiftKey; // should be constant in a scope to avoid incorrect ref in nextTick
                process.nextTick(() => this.handleGoToNextField(field, backward));
                e.preventDefault();
                break;
            default:
        }
    });
}
